import React, { useMemo, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { ArrowLeft, RefreshCw, Play, Undo2 } from "lucide-react"
import moment from "moment"

import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchCard from "~/library/CatchCard"

import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import { useToast } from "~/utils/Toast"
import { apiWorkspaceImports } from "~/routes"

import {
    Import,
    ImportMessage,
    normalizeMessages,
    filterToLastRun,
    extractCreatedCounts,
    filterByQuery,
    isError,
    lastRunAt,
    niceDate,
    postImportAction,
} from "~/utils/imports"

const ImportView: React.FC = () => {
    const { venueSlug } = getAppConfig()
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const toast = useToast()

    const endpoint = apiWorkspaceImports.show.path({ venue_slug: venueSlug, id: id! })
    const { data: imp, isLoading, refetch } = useFetchResource<Import, "import">(endpoint, "import", {})

    const [qErrors, setQErrors] = useState("")
    const [showLog, setShowLog] = useState(false)
    const [isRunning, setIsRunning] = useState(false)
    const [isRollingBack, setIsRollingBack] = useState(false)

    const allMessages = useMemo(() => normalizeMessages(imp?.messages), [imp?.messages])
    const { messages: lastRunMessages } = useMemo(() => filterToLastRun(allMessages), [allMessages])
    const createdStats = useMemo(() => extractCreatedCounts(lastRunMessages), [lastRunMessages])

    const errorMessages = useMemo(
        () => filterByQuery(lastRunMessages.filter(isError), qErrors),
        [lastRunMessages, qErrors]
    )

    const lastRunAtDate = useMemo(() => lastRunAt(allMessages), [allMessages])

    const canRun = !!imp?.id && (imp.status === "pending" || imp.status === "failed")
    const canRollback =
        !!imp?.id && (imp.status === "completed" || imp.status === "completed_with_errors" || imp.status === "failed")
    const busy = isRunning || isRollingBack || imp?.status === "processing"

    const handleRun = async () => {
        if (!imp?.id) return
        setIsRunning(true)
        try {
            const url = apiWorkspaceImports.run.path({ venue_slug: venueSlug, id: imp.id })
            const res = await postImportAction({ url })

            if (res.ok) {
                toast("Import started", "success")
                await refetch?.()
            } else {
                toast(res.error || "Run failed. Please try again", "danger")
            }
        } catch (e) {
            console.error(e)
            toast("Run failed. Please try again", "danger")
        } finally {
            setIsRunning(false)
        }
    }

    const handleRollback = async () => {
        if (!imp?.id) return
        setIsRollingBack(true)
        try {
            const url = apiWorkspaceImports.rollback.path({ venue_slug: venueSlug, id: imp.id })
            const res = await postImportAction({ url })

            if (res.ok) {
                toast("Rollback completed", "success")
                await refetch?.()
            } else {
                toast(res.error || "Rollback failed. Please try again", "danger")
            }
        } catch (e) {
            console.error(e)
            toast("Rollback failed. Please try again", "danger")
        } finally {
            setIsRollingBack(false)
        }
    }

    if (isLoading) return <LoadingWrapper isLoading />
    if (!imp?.id) return <div className="text-muted p-3">Not found</div>

    return (
        <>
            <PageTitle
                title={
                    <div className="d-flex align-items-center w-100">
                        <button
                            type="button"
                            className="btn btn-link p-0 me-2"
                            onClick={() => navigate("/imports")}
                            aria-label="Back to Imports"
                            title="Back to Imports"
                        >
                            <ArrowLeft />
                        </button>

                        <span>Import: {imp.file_name || imp.id}</span>
                    </div>
                }
            />

            <div className="row">
                <div className="col-12">
                    <CatchCard title="Summary">
                        <div className="row">
                            <div className="col-3">
                                <b>Status</b>
                                <div>{imp.status || "—"}</div>
                            </div>
                            <div className="col-3">
                                <b>Created</b>
                                <div>{niceDate(imp.created_at)}</div>
                            </div>
                            <div className="col-3">
                                <b>Updated</b>
                                <div>{niceDate(imp.updated_at)}</div>
                            </div>
                            <div className="col-3">
                                <b>Errors</b>
                                <div>{errorMessages.length}</div>
                            </div>
                        </div>

                        <div className="mt-2 text-muted">
                            <b>Last run</b>: {lastRunAtDate ? niceDate(lastRunAtDate) : "—"}
                        </div>

                        <div className="mt-3 d-flex gap-2 align-items-center flex-wrap">
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => refetch?.()}>
                                <RefreshCw size={16} className="me-1" />
                                Refresh
                            </button>

                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setShowLog((s) => !s)}>
                                {showLog ? "Hide detailed log" : "Show detailed log"}
                            </button>

                            <div className="ms-auto d-flex gap-2">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    disabled={!canRun || busy}
                                    onClick={handleRun}
                                >
                                    <Play size={16} className="me-1" />
                                    {isRunning ? "Running…" : "Run"}
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    disabled={!canRollback || busy}
                                    onClick={handleRollback}
                                >
                                    <Undo2 size={16} className="me-1" />
                                    {isRollingBack ? "Rolling…" : "Rollback"}
                                </button>
                            </div>
                        </div>
                    </CatchCard>
                </div>

                <div className="col-12 mt-3">
                    <CatchCard title="Created records">
                        <div className="text-muted">
                            Total created: <b>{createdStats.total}</b>
                        </div>

                        {createdStats.entries.length > 0 ? (
                            <div className="table-responsive mt-2">
                                <table className="table table-sm align-middle">
                                    <thead>
                                    <tr>
                                        <th>Model</th>
                                        <th style={{ width: 140 }} className="text-end">
                                            Count
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {createdStats.entries.map((e) => (
                                        <tr key={e.model}>
                                            <td>{e.model}</td>
                                            <td className="text-end">
                                                <span className="badge bg-secondary">{e.count}</span>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-muted mt-2">No created records found in last run messages</div>
                        )}
                    </CatchCard>
                </div>

                <div className="col-12 mt-3">
                    <CatchCard title="Errors">
                        <div className="d-flex gap-2 mb-2 align-items-center">
                            <input
                                className="form-control"
                                placeholder="Search in errors…"
                                value={qErrors}
                                onChange={(e) => setQErrors(e.target.value)}
                            />
                            <div className="text-muted ms-auto">Showing {errorMessages.length}</div>
                        </div>

                        <div className="table-responsive">
                            <table className="table table-sm align-middle">
                                <thead>
                                <tr>
                                    <th style={{ width: 170 }}>Time</th>
                                    <th style={{ width: 180 }}>Sheet</th>
                                    <th style={{ width: 140 }}>Entity</th>
                                    <th style={{ width: 80 }}>Row</th>
                                    <th style={{ width: 220 }}>Identifier</th>
                                    <th>Message</th>
                                </tr>
                                </thead>
                                <tbody>
                                {errorMessages.map((m: ImportMessage, i: number) => (
                                    <tr key={`${m.at || "na"}-${i}`}>
                                        <td className="text-muted">{m.at ? moment(m.at).format("MMM Do YYYY, HH:mm:ss") : "—"}</td>
                                        <td>{m.sheet || "—"}</td>
                                        <td>{m.entity || "—"}</td>
                                        <td>{m.row ?? "—"}</td>
                                        <td>
                                            <code>{m.identifier || "—"}</code>
                                        </td>
                                        <td style={{ whiteSpace: "pre-wrap" }}>{m.message || "—"}</td>
                                    </tr>
                                ))}

                                {errorMessages.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="text-muted">
                                            No errors
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>
                    </CatchCard>
                </div>

                {showLog && (
                    <div className="col-12 mt-3">
                        <CatchCard title="Detailed log (last run)">
                            <div className="table-responsive">
                                <table className="table table-sm align-middle">
                                    <thead>
                                    <tr>
                                        <th style={{ width: 170 }}>Time</th>
                                        <th style={{ width: 120 }}>Level</th>
                                        <th style={{ width: 180 }}>Sheet</th>
                                        <th style={{ width: 140 }}>Entity</th>
                                        <th style={{ width: 80 }}>Row</th>
                                        <th style={{ width: 220 }}>Identifier</th>
                                        <th>Message</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {lastRunMessages.map((m: ImportMessage, i: number) => (
                                        <tr key={`${m.at || "na"}-${i}`}>
                                            <td className="text-muted">{m.at ? moment(m.at).format("MMM Do YYYY, HH:mm:ss") : "—"}</td>
                                            <td>{m.level || "info"}</td>
                                            <td>{m.sheet || "—"}</td>
                                            <td>{m.entity || "—"}</td>
                                            <td>{m.row ?? "—"}</td>
                                            <td>
                                                <code>{m.identifier || "—"}</code>
                                            </td>
                                            <td style={{ whiteSpace: "pre-wrap" }}>{m.message || "—"}</td>
                                        </tr>
                                    ))}

                                    {lastRunMessages.length === 0 && (
                                        <tr>
                                            <td colSpan={7} className="text-muted">
                                                No log messages for last run
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        </CatchCard>
                    </div>
                )}
            </div>
        </>
    )
}

export default ImportView
